"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ArrowRight, Award, Search, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP);
export default function Nationalseo() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const tl = gsap.timeline({
        defaults: {
          ease: "power3.out",
          duration: 0.8
        }
      });
      tl.from(".hero-badge", {
        opacity: 0,
        y: -16
      }).from(".hero-title", {
        opacity: 0,
        y: 24
      }, "-=0.5").from(".hero-description", {
        opacity: 0,
        y: 20
      }, "-=0.5").from(".hero-trust", {
        opacity: 0,
        y: 12
      }, "-=0.3").from(".hero-actions", {
        opacity: 0,
        y: 16
      }, "-=0.4").from(".hero-visual", {
        opacity: 0,
        scale: 0.96
      }, "-=0.6");
    });
  }, {
    scope: containerRef
  });
  const valuePoints = ["Tailored nationwide SEO strategy aligned with high-intent commercial keywords", "Technical quality, content relevance, and authority building delivered directly by me", "Transparent performance reporting with no artificial ranking guarantees"];
  return <section id="nationalseohero" data-nav="light" ref={containerRef} className="relative overflow-hidden bg-gradient-to-b from-background via-background to-primary/5 py-16 md:py-24 lg:py-28">
      {/* Decorative ambient background glows */}
      <div className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-accent/10 blur-3xl" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          {/* Left Column: Editorial & Value Proposition */}
          <div className="lg:col-span-7">
            <div className="hero-badge mb-4">
              <Badge variant="secondary" className="gap-2 border border-primary/20 bg-secondary px-3.5 py-1.5 text-xs font-semibold tracking-wide text-foreground uppercase">
                <Search className="size-3.5 text-primary" />
                <span>Bespoke National SEO Strategy</span>
              </Badge>
            </div>

            <h1 className="hero-title text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              National SEO
            </h1>

            <p className="hero-description mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
              As an independent SEO consultant, I help businesses optimise their website to rank for commercially valuable, non-location-specific search terms across the entire country rather than a single town or city. My national SEO campaigns cover technical site health, content relevance, authority building, and user experience—focused on driving sustainable organic growth with no false ranking guarantees.
            </p>

            {/* Value Points */}
            <ul className="hero-trust mt-6 space-y-2.5">
              {valuePoints.map((point, i) => <li key={i} data-index={i} className="flex items-center gap-2.5 text-sm font-medium text-foreground sm:text-base">
                  <CheckCircle2 className="size-5 shrink-0 text-accent" />
                  <span>{point}</span>
                </li>)}
            </ul>

            {/* CTAs */}
            <div className="hero-actions mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <Button asChild size="lg" className="rounded-lg shadow-md font-semibold">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-lg font-semibold border-border bg-background hover:bg-muted">
                <Link to="/CaseStudiesPage">
                  Explore My Case Studies
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Authentic Visual Card */}
          <div className="lg:col-span-5">
            <div className="hero-visual relative mx-auto max-w-md lg:max-w-none">
              <div className="relative rounded-2xl border border-border bg-card p-4 shadow-xl sm:p-6">
                <div className="relative aspect-4/5 overflow-hidden rounded-xl bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/Seo-Consultant.png-wvc-sizes" alt="Yassine Essaadi - Dedicated UK and National SEO Consultant" loading="lazy" decoding="async" className="size-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-transparent to-transparent" />
                  
                  <div className="absolute bottom-4 left-4 right-4 text-background">
                    <p className="text-xs uppercase tracking-wider font-medium text-secondary">
                      Independent SEO Consultant
                    </p>
                    <p className="text-lg font-bold">Yassine Essaadi</p>
                    <p className="text-xs text-background/80">
                      Founder, YE Consultancy
                    </p>
                  </div>
                </div>

                {/* Micro trust card */}
                <div className="mt-4 rounded-lg bg-background p-4 border border-border shadow-xs flex items-center gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent">
                    <Award className="size-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Dedicated Focus
                    </p>
                    <p className="text-sm font-semibold text-foreground">
                      Direct national search strategy with me
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}